/**
 * Workflow trigger normalization and display helpers.
 */

import {
  createTriggerSelectionFromCatalog,
  findTriggerCatalogEntry,
  resolveTriggerFromLegacy,
} from './workflowTriggerCatalog';

export function getTriggerTitle(trigger) {
  if (!trigger) return '';
  const resolved = resolveTriggerFromLegacy(trigger);
  if (resolved?.name) return resolved.name;
  return trigger.name
    || trigger.trigger_name
    || trigger.label
    || (trigger.id ? String(trigger.id).replace(/[._]/g, ' ') : '');
}

export function getTriggerEventId(trigger) {
  if (!trigger) return '';
  const resolved = resolveTriggerFromLegacy(trigger);
  if (resolved?.eventKey) return resolved.eventKey;
  return trigger.key || trigger.trigger_type || trigger.event_id || trigger.id || '';
}

function formatFilterLine(filter) {
  if (!filter?.field) return '';
  const fieldLabel = filter.field_label || filter.field.replace(/_/g, ' ');
  const operator = (filter.operator || 'equals').replace(/_/g, ' ');
  const value = filter.value;
  if (filter.operator === 'exists' || filter.operator === 'not_exists') {
    return `${fieldLabel} ${operator}`;
  }
  if (value === undefined || value === null || value === '') {
    return `${fieldLabel} ${operator}`;
  }
  return `${fieldLabel} ${operator} "${value}"`;
}

export function getTriggerFilterSummary(trigger) {
  const filters = trigger?.config?.filters || trigger?.filters || [];
  if (!filters.length) return 'No filters applied';

  const lines = filters.map(formatFilterLine).filter(Boolean);
  if (!lines.length) return 'No filters applied';

  if (lines.length === 1) return lines[0];
  return `${lines[0]}${lines.length > 1 ? ` (+${lines.length - 1} more)` : ''}`;
}

export function getTriggerConditionText(trigger) {
  const summary = getTriggerFilterSummary(trigger);
  if (summary === 'No filters applied') return null;
  return summary;
}

export function normalizeWorkflowTrigger(trigger, index = 0) {
  if (!trigger) return null;
  const resolved = resolveTriggerFromLegacy(trigger);
  const catalogEntry = resolved || findTriggerCatalogEntry(trigger);
  const eventKey = resolved?.eventKey
    || catalogEntry?.eventKey
    || trigger.key
    || trigger.trigger_type
    || trigger.event_id
    || trigger.id;
  const id = trigger.id || eventKey || `trigger_${index + 1}`;
  const title = resolved?.name || catalogEntry?.name || getTriggerTitle(trigger);
  const description = resolved?.description || catalogEntry?.description || trigger.description;
  return {
    ...trigger,
    id,
    name: title,
    label: title,
    description,
    trigger_type: eventKey,
    event_id: eventKey,
    config: {
      ...(trigger.config || {}),
      ...(catalogEntry || resolved
        ? {
          event_key: eventKey,
          trigger_key: eventKey,
          event: eventKey,
          description: description || trigger.config?.description,
        }
        : {}),
    },
  };
}

export function normalizeWorkflowTriggers(workflow) {
  if (!workflow) return [];
  // Respect an explicit triggers array even when empty so deletions are not
  // resurrected from legacy trigger / trigger_event fields.
  if (Array.isArray(workflow.triggers)) {
    return workflow.triggers.map(normalizeWorkflowTrigger).filter(Boolean);
  }
  if (workflow.trigger) {
    const normalized = normalizeWorkflowTrigger(workflow.trigger, 0);
    return normalized ? [normalized] : [];
  }
  if (workflow.trigger_event) {
    return [normalizeWorkflowTrigger({
      id: workflow.trigger_event,
      name: workflow.trigger_event.replace(/[._]/g, ' '),
      trigger_type: workflow.trigger_event,
    }, 0)];
  }
  return [];
}

export function syncLegacyTriggerFields(workflow, triggers) {
  const list = triggers || normalizeWorkflowTriggers(workflow);
  const primary = list[0] || null;
  return {
    ...workflow,
    triggers: list,
    trigger: primary
      ? {
        id: getTriggerEventId(primary) || primary.id,
        name: getTriggerTitle(primary),
        label: primary.label || getTriggerTitle(primary),
        type: primary.type || getTriggerEventId(primary) || primary.id,
        config: primary.config,
      }
      : null,
    trigger_event: primary ? (getTriggerEventId(primary) || primary.id) : null,
  };
}

/**
 * Canonical persisted trigger structure shared with the backend:
 *   { key, type, label, filters }
 * `type` is the trigger category (e.g. "authentication"), `key` the event key.
 */
export function toCanonicalWorkflowTrigger(trigger) {
  if (!trigger) return null;
  const normalized = normalizeWorkflowTrigger(trigger);
  if (!normalized) return null;
  const key = getTriggerEventId(normalized) || normalized.id;
  if (!key) return null;
  const filters = normalized.config?.filters || normalized.filters || [];
  // Some legacy shapes store the event key in `type`; only treat `type` as a
  // category when it is a distinct value (e.g. "authentication").
  const rawType = typeof normalized.type === 'string' ? normalized.type : '';
  const category = normalized.category
    || (rawType && rawType !== key && rawType !== 'trigger' ? rawType : '')
    || 'system';
  return {
    key,
    type: category,
    label: getTriggerTitle(normalized) || String(key).replace(/[._]/g, ' '),
    filters: Array.isArray(filters) ? filters : [],
  };
}

/**
 * Build the trigger-related fields of a workflow save payload.
 * Always sends explicit values (null / []) so removal persists, while the
 * backend preserves the stored trigger when these fields are omitted.
 */
export function buildTriggerSaveFields(workflow) {
  const triggers = normalizeWorkflowTriggers(workflow);
  const canonical = triggers.length ? toCanonicalWorkflowTrigger(triggers[0]) : null;
  return {
    trigger: canonical,
    trigger_event: canonical ? canonical.key : null,
    triggers,
  };
}

/**
 * Hydrate the triggers list from a workflow document returned by the API.
 * Prefers the rich `triggers` array; falls back to the legacy `trigger`
 * object / `trigger_event` string so older documents still load. An absent or
 * empty `triggers` array from the server must NOT wipe a legacy trigger.
 */
export function hydrateWorkflowTriggersFromServer(wf) {
  if (!wf) return [];
  if (Array.isArray(wf.triggers) && wf.triggers.length > 0) {
    return wf.triggers.map(normalizeWorkflowTrigger).filter(Boolean);
  }
  const { triggers: _ignoredEmptyTriggers, ...rest } = wf;
  return normalizeWorkflowTriggers(rest);
}

function createUniqueTriggerId() {
  return `trigger_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function createTriggerFromSelection(selection, options = {}) {
  const { preserveId = null } = options;
  const catalogSelection = createTriggerSelectionFromCatalog(selection);
  // Merge the user's configuration (e.g. filters) over the catalog defaults so
  // configured filters are not lost when the selection matches a catalog entry.
  const base = catalogSelection
    ? {
      ...catalogSelection,
      ...(Array.isArray(selection?.filters) ? { filters: selection.filters } : {}),
      config: {
        ...catalogSelection.config,
        ...(selection?.config || {}),
      },
    }
    : selection;
  const title = getTriggerTitle(base);
  return normalizeWorkflowTrigger({
    ...base,
    id: preserveId || createUniqueTriggerId(),
    name: title,
    label: base.label || title,
  });
}

export function duplicateWorkflowTrigger(trigger) {
  const copy = JSON.parse(JSON.stringify(trigger));
  copy.id = createUniqueTriggerId();
  const baseName = getTriggerTitle(trigger);
  copy.name = `${baseName} (copy)`;
  copy.trigger_name = copy.name;
  copy.label = copy.name;
  return normalizeWorkflowTrigger(copy);
}

export const EMPTY_TRIGGER_STATS = {
  attempted: 0,
  matched: 0,
  unmatched: 0,
  matchedRate: 0,
  rows: [],
  total: 0,
  page: 1,
  pageSize: 10,
};
